import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import api, { API_URL } from '../services/api';
import { toast } from 'react-hot-toast';

// Update styles to better control scrolling
const styles = {
  modalBody: {
    padding: 0,
    height: '70vh',
    display: 'flex',
    flexDirection: 'column'
  },
  container: {
    flex: 1,
    position: 'relative',
    backgroundColor: '#f5f5f5',
    borderRadius: '4px'
  },
  viewer: {
    width: '100%',
    height: '100%',
    border: 'none',
    backgroundColor: '#fff'
  },
  imageContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  image: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain'
  }
};

function DocumentViewerModal({ show, onHide, document: documentData }) {
  const [loading, setLoading] = useState(true);
  const [isImage, setIsImage] = useState(false);
  const [isPdf, setIsPdf] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');
  const [fileData, setFileData] = useState(null);
  const [error, setError] = useState(null);
  const [useObjectTag, setUseObjectTag] = useState(false);
  const [key, setKey] = useState(0); // Add key for forcing remount

  // Reset everything when modal opens/closes
  useEffect(() => {
    if (show) {
      setLoading(true);
      setError(null);
      setDocumentUrl('');
      setFileData(null);
      setKey(prev => prev + 1); // Force remount when modal opens
    }
  }, [show]);

  useEffect(() => {
    if (documentData) {
      try {
        // Reset key to force remount
        setKey(prev => prev + 1);
        
        const fileName = documentData.fileName?.toLowerCase() || '';
        
        const isPdfFile = 
          fileName.endsWith('.pdf') || 
          (documentData.fileData && documentData.fileData.includes('application/pdf')) ||
          (documentData.type && documentData.type.includes('pdf'));
        
        const isImgFile = 
          fileName.endsWith('.jpg') || 
          fileName.endsWith('.jpeg') || 
          fileName.endsWith('.png') || 
          fileName.endsWith('.gif') ||
          (documentData.fileData && documentData.fileData.includes('image/'));
        
        setIsImage(isImgFile);
        setIsPdf(isPdfFile);
        
        if (documentData.fileData) {
          setFileData(documentData.fileData);
          setLoading(false);
        } else if (documentData.blobUrl) {
          fetchAndCreateBlobUrl(documentData.blobUrl, isPdfFile);
        } else if (documentData.url) {
          setDocumentUrl(documentData.url);
          setLoading(false);
        } else if (documentData.fileName) {
          const apiUrl = `${API_URL}/api/bols/documents/${documentData.fileName}`;
          if (isPdfFile) {
            fetchAndCreateBlobUrl(apiUrl, true);
          } else {
            setDocumentUrl(apiUrl);
            setLoading(false);
          }
        } else {
          throw new Error('No valid document source found');
        }
        
        setError(null);
      } catch (err) {
        console.error('Error setting up document viewer:', err);
        setError('Failed to prepare document for viewing');
        setLoading(false);
      }
    }
  }, [documentData]);

  const fetchAndCreateBlobUrl = async (url, isPdf) => {
    try {
      // Handle different URL types properly
      let apiPath;
      if (url.startsWith('blob:') || url.startsWith('http://') || url.startsWith('https://')) {
        // Already a complete URL (blob, http, or https) - use fetch for external URLs
        const response = await fetch(url, {
          credentials: 'include'
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const responseBlob = await response.blob();
        const blob = new Blob([responseBlob], { 
          type: isPdf ? 'application/pdf' : response.headers.get('content-type') 
        });
        const blobUrl = URL.createObjectURL(blob);
        setDocumentUrl(blobUrl);
        setUseObjectTag(isPdf);
        setLoading(false);
        return;
      } else {
        // Relative URL, use api instance
        apiPath = url;
      }
      
      const response = await api.get(apiPath, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { 
        type: isPdf ? 'application/pdf' : response.headers['content-type'] 
      });
      const blobUrl = URL.createObjectURL(blob);

      setDocumentUrl(blobUrl);
      setUseObjectTag(isPdf);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching document:', error);
      setError('Failed to load document');
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (documentUrl && documentUrl.startsWith('blob:')) {
        URL.revokeObjectURL(documentUrl);
      }
    };
  }, [documentUrl]);

  const downloadDocument = () => {
    if (fileData) {
      const link = document.createElement('a');
      link.href = fileData;
      link.download = documentData?.fileName || 'document.pdf';
      link.click();
    } else if (documentUrl) {
      const link = document.createElement('a');
      link.href = documentUrl;
      link.download = documentData?.fileName || 'document.pdf';
      link.click();
    }
  };
  
  const openInNewTab = () => {
    if (fileData) {
      window.open(fileData, '_blank');
    } else if (documentUrl) {
      window.open(documentUrl, '_blank');
    }
  };

  const resetScroll = () => {
    try {
      // For PDF viewers
      const iframe = window.document.querySelector('iframe');
      if (iframe && iframe.contentWindow) {
        try {
          // Get the iframe's document
          const doc = iframe.contentWindow.document;
          
          // Common PDF viewer element IDs and classes
          const selectors = [
            '#viewerContainer',
            '#viewer',
            '.pdfViewer',
            '#outerContainer',
            '#mainContainer',
            '.view',
            '.page'
          ];
          
          // Try to scroll each potential element
          selectors.forEach(selector => {
            const element = doc.querySelector(selector);
            if (element) {
              element.scrollTop = 0;
              if (element.parentElement) {
                element.parentElement.scrollTop = 0;
              }
            }
          });

          // Also try to scroll the main elements
          if (doc.documentElement) doc.documentElement.scrollTop = 0;
          if (doc.body) doc.body.scrollTop = 0;
          
          // Force scroll using scrollTo
          iframe.contentWindow.scrollTo({
            top: 0,
            left: 0,
            behavior: 'instant'
          });
        } catch (e) {
          // Ignore cross-origin errors
        }
      }

      // For object tag
      const object = window.document.querySelector('object');
      if (object) {
        try {
          object.scrollTop = 0;
          if (object.contentDocument) {
            object.contentDocument.documentElement.scrollTop = 0;
            object.contentDocument.body.scrollTop = 0;
          }
        } catch (e) {
          // Ignore errors
        }
      }

      // Reset modal body
      const modalBody = window.document.querySelector('.modal-body');
      if (modalBody) modalBody.scrollTop = 0;
    } catch (err) {
      console.log('Scroll reset attempted');
    }
  };

  // Reset scroll when modal opens
  useEffect(() => {
    if (show) {
      // Initial reset
      resetScroll();
      // Multiple attempts to catch after content loads
      const attempts = [100, 300, 500];
      attempts.forEach(delay => {
        setTimeout(resetScroll, delay);
      });
    }
  }, [show]);

  // Reset when document changes
  useEffect(() => {
    if (documentUrl || fileData) {
      resetScroll();
    }
  }, [documentUrl, fileData]);

  // Modify the URL for PDFs to force scroll to top
  const getDocumentUrl = (url) => {
    if (!url) return '';
    if (isPdf) {
      // Add PDF viewer parameters to force scroll to top
      const separator = url.includes('?') ? '&' : '#';
      return `${url}${separator}page=1&view=FitH,0&scrollbar=0,0`;
    }
    return url;
  };

  if (!documentData) return null;

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      centered 
      dialogClassName="document-viewer-modal"
      onEntered={resetScroll}
    >
      <Modal.Header closeButton>
        <Modal.Title>{documentData?.descr || 'Document Viewer'}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={styles.modalBody}>
        {loading && (
          <div className="d-flex justify-content-center align-items-center flex-grow-1">
            <Spinner animation="border" variant="primary" />
          </div>
        )}
        
        {error && (
          <div className="alert alert-warning m-3">
            <p>{error}</p>
            {(documentUrl || fileData) && (
              <Button 
                variant="primary" 
                onClick={openInNewTab}
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                Open in New Tab
              </Button>
            )}
          </div>
        )}

        {!loading && !error && isImage && (
          <div style={styles.imageContainer}>
            <img 
              src={fileData || documentUrl} 
              alt={documentData?.descr || 'Document'} 
              style={styles.image}
              onError={() => setError('Failed to load image')}
              onLoad={resetScroll}
            />
          </div>
        )}
        
        {!loading && !error && !isImage && (
          <div style={styles.container}>
            {useObjectTag ? (
              <object
                data={documentUrl}
                type="application/pdf"
                style={styles.viewer}
                onLoad={resetScroll}
              >
                <p>Unable to display PDF. <a href={documentUrl} target="_blank" rel="noopener noreferrer">Click here to download</a></p>
              </object>
            ) : (
              <iframe
                src={documentUrl}
                title="Document Viewer"
                style={styles.viewer}
                onLoad={resetScroll}
              >
                <p>Unable to display document. <a href={documentUrl} target="_blank" rel="noopener noreferrer">Click here to download</a></p>
              </iframe>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="outline-primary" 
          onClick={openInNewTab}
          disabled={!documentUrl && !fileData}
          className="me-auto"
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
          Open in New Tab
        </Button>
        
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        
        <Button 
          variant="primary" 
          onClick={downloadDocument}
          disabled={!documentUrl && !fileData}
        >
          <FontAwesomeIcon icon={faDownload} className="me-2" />
          Download
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default DocumentViewerModal; 